import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import { IconComponent } from '../icon/icon';

/**
 * The frame of every form modal: heading, close button, scrolling body, refusal
 * banner and footer. The dialog component that uses it owns its fields and its
 * write, and closes itself only once the write succeeds (FLOW-378):
 *
 *   <app-modal heading="…" submitLabel="Save" [formGroup]="form"
 *              [pending]="pending()" [error]="error()" (submitted)="save()">
 *     …fields…
 *   </app-modal>
 *
 * It renders the `<form>`, so Enter submits; a `[formGroup]` on the host sees the
 * submit event bubble and shows its errors. Don't project a second `<form>`, and
 * give a projected button `type="button"`.
 *
 * Dismissable throughout via Cancel, the X, Escape or the overlay. The X comes
 * last in the DOM so a modal's initial focus lands on its first field.
 *
 * Without a `submitLabel` it is a modal that only reads: no `<form>`, and one
 * *Close* button. `dismissWith` takes Cancel and the X over, for a dialog that
 * asks before losing what was typed (`guardTypedInput`).
 */
@Component({
  selector: 'app-modal',
  imports: [
    NgTemplateOutlet,
    MatButtonModule,
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    MatProgressSpinnerModule,
    MatTooltipModule,
    IconComponent,
  ],
  templateUrl: './modal.html',
  styleUrl: './modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalComponent {
  readonly heading = input.required<string>();
  /** One line under the heading: what this changes, or why it is being asked. */
  readonly description = input<string>();
  /** The submit button's words. Leave it out for a modal that only reads. */
  readonly submitLabel = input<string>();
  /** True while the write is in flight. Submit waits; dismissing still works. */
  readonly pending = input(false);
  /** A refusal of the act as a whole. One about a field goes on the field (`placeRefusal`). */
  readonly error = input<string | null>(null);
  /** Runs in place of closing when Cancel or the X is pressed. */
  readonly dismissWith = input<(() => void) | null>(null);
  readonly submitted = output<void>();

  private readonly dialogRef = inject(MatDialogRef);

  protected submit(event: Event): void {
    event.preventDefault();
    if (!this.pending()) this.submitted.emit();
  }

  protected dismiss(): void {
    const guard = this.dismissWith();
    if (guard) guard();
    else this.dialogRef.close();
  }
}
